import { Component, OnInit, Input, HostListener, Renderer2, ElementRef, ChangeDetectorRef, ViewChild, EventEmitter, Output } from '@angular/core';
import { Options, SelectConfig } from '../models';
import { ToastService } from '../toast';

@Component({
  // tslint:disable-next-line:component-selector
  selector: 'q-select',
  templateUrl: './select.component.html',
  styleUrls: ['./select.component.css'],
})

export class SelectComponent implements OnInit {
  // #region constructor
  /**
  * @ignore
  */
  constructor(
    private render: Renderer2,
    private element: ElementRef,
    private cd: ChangeDetectorRef,
    private toastservice: ToastService
  ) { }
  // #endregion

  // #region Variables publics
  /**
   * boolean qui s'active et se désactive au passage de la souris sur l'élément
   */
  public isActive: boolean;
  /**
   * Option sélectionné
   */
  public value: Options;
  /**
   * Utilisé uniquement si l'option de filtre est à true
   *
   * Option sélectionné dans le cas ou l'on utilise la fonction de recherche
   */
  public temporyFilter: Options;
  /**
   * Utilisé uniquement si l'option de filtre est à true
   *
   * compteur permettant de savoir à quel index on se trouve dans la list des options filtrés lorsque l'on descend ou monte avec le clavier
   */
  public compteur: number;
  /**
   * Utilisé uniquement si l'option de filtre est à true
   *
   * tableau des options filtrés
   */
  public searchOption: Array<Options>;
  /**
   * Utilisé uniquement si l'option de filtre est à true
   *
   * boolean qui s'active et se désactive au passage de la souris sur la liste
   */
  public mouseIn: boolean;
  /**
   * Indique si la liste est fermé, ouverte ou doit resté ouverte
   */
  public list: string;
  // #endregion

  // #region input et output

  /**
   * retourne la value de l'option selectionnée
   */
  @Output() outData: EventEmitter<Options | string> = new EventEmitter<Options | string>();

  /**
   * liste des options
   */
  @Input('options') options: Array<Options>;

  /**
   * permet de configurer des classes de personnalisation du select
   */
  @Input('style') style: SelectConfig;

  /**
   * permet de connaitre la width et la height de l'élément
   */
  @Input('sizeElement') sizeElement: { width: number, height: number };

  /**
   * option permettant d'activer ou non le filtre
   */
  @Input('isFilter') isFilter: boolean;

  /**
   * option permettant de savoir si l'on retourne dans l'output la value de l'option ou l'option entierement
   */
  @Input('isValue') isValue: boolean;

  /**
   * option permettant de paramétrer le placeholder dans le cas ou l'option de recherche serait activé
   */
  @Input('placeHolder') placeHolder: string;

  /**
   * propriété qui stock la value dans le cas d'une recherche
   */
  @Input('filter') filter: string;

  /**
   * propriété qui indique la taille et le positionnement de la liste
   */
  @Input('placement') placement: string;

  /**
   * propriété qui gere les classes
   */
  @Input('class') class: string;

  /**
   * permet d'empecher la selection du select'
  */
  @Input('disabled') disabled: boolean;
  /**
   * permet de dire si la selection d'une option est obligatoire'
  */
  @Input('required') required: string;
  // #endregion

  // #region viewChild
  /**
   * viewchild de la liste
   */
  @ViewChild('listElement', { static: false }) listElement: ElementRef;

  /**
   * viewchild de l'élément global
   */
  @ViewChild('globalElement', { static: false }) globalElement: ElementRef;

  /**
   * viewchild de l'input global
   */
  @ViewChild('input', { static: false }) input: ElementRef;
  // #endregion

  // #region Events
  /**
   * surveille les click et fermer la list en fonction de la position de la souris au moment du click
   */
  @HostListener('window:click') onClick(): void {
    if (!this.isActive && this.list) {
      this.list = 'close';
      this.closeList();
    }
  }

  /**
   * evenement de mouseover
   */
  @HostListener('mouseover') Mouseover(): void {
    this.isActive = true;
  }

  /**
   * evenement de mouseout
   */
  @HostListener('mouseout') Mouseout(): void {
    this.isActive = false;
  }

  /**
   * evenement de resize de l'élément
   */
  @HostListener('resize') Resize(): void {
    this.closeList();
  }

  /**
   * surveille l'évenement de resize au niveau du navigateur
   */
  @HostListener('window:resize') WResize(): void {
    this.closeList();
  }
  // #endregion

  // #region cycle de vie
  /**
   * @ignore
   */
  ngOnInit(): void {
    this.temporyFilter = new Options();
    this.compteur = 0;
    this.list = 'close';
    this.isActive = false;
    this.mouseIn = false;
    this.value = this.options.filter((element: Options) => element.selected)[0];
    this.cd.detectChanges();
  }
  // #endregion

  // #region fonctions publics

  /**
   * action lorsque 'lon clique sur le bouton d'ouverture du select
   */
  public clickArrow(): void {
    if (this.list === 'open' || this.list === 'stayOpen') {
      this.closeList();
    } else if (this.list === 'close') {
      this.showList();
    }
    if (this.isFilter) {
      this.filter = '';
    }
  }

  /**
   * ouverture de la liste
   */
  public showList(): void {
    if (!this.disabled) {
      if (this.list === 'close') {
        this.list = 'open';
        if (this.isFilter) {
          this.input.nativeElement.focus();
          this.temporyFilter = new Options();
        }
        const body: HTMLElement = document.body;
        const top: string = `${this.getOffset(this.element).top + this.sizeElement.height}px`;
        const left: string = `${this.getOffset(this.element).left}px`;
        const right: string = `${window.innerWidth - (this.getOffset(this.element).left + this.globalElement.nativeElement.clientWidth)}px`;
        const width: string = `${this.globalElement.nativeElement.clientWidth}px`;

        this.render.setStyle(this.listElement.nativeElement, 'top', top);
        if (this.placement === 'left') {
          this.render.setStyle(this.listElement.nativeElement, 'left', left);
          this.render.setStyle(this.listElement.nativeElement, 'min-width', width);
          this.render.setStyle(this.listElement.nativeElement, 'max-width', 'auto');
        } else if (this.placement === 'right') {
          this.render.setStyle(this.listElement.nativeElement, 'right', right);
          this.render.setStyle(this.listElement.nativeElement, 'min-width', width);
          this.render.setStyle(this.listElement.nativeElement, 'max-width', 'auto');
        } else if (this.placement === 'container') {
          this.render.setStyle(this.listElement.nativeElement, 'left', left);
          this.render.setStyle(this.listElement.nativeElement, 'width', width);
        }

        this.render.appendChild(body, this.listElement.nativeElement);
        this.render.removeClass(this.listElement.nativeElement, 'd-none');
        this.compteur = 0;
        this.cd.detectChanges();
      } else if (this.list === 'open') {
        this.closeList();
      }
    } else {
      this.list = 'close';
    }
  }

  /**
   * Action lorsque l'on selectionne une option de la liste
  */
  public selectedValue(data: Options, e: any, i: number): void {
    if (this.required === 'require' || this.required === 'no-valid') {
      if (i === 0) {
        this.required = 'no-valid';
        this.toastservice.showAlertToast('Vous devez sélectionner un élément de la liste');
      } else {
        this.required = 'require';
      }
    }
    this.value = data;
    if (this.isValue) {
      this.outData.emit(this.value.value);
    } else {
      this.outData.emit(this.value);
    }
    this.closeList();
    if (this.isFilter) {
      this.compteur = 0;
      this.filter = data.text;
      this.temporyFilter = new Options();
    }
  }

  /**
  * Action lorsquel'on appuie sur une touche du clavier en mode recherche
  */
  public keydown(e: any): void {
    if (e.code === 'Backslash' || e.code === 'NumpadMultiply') {
      e.preventDefault();
      this.filter = '';
    } else {
      // On crée une nouvelle liste d'options pour stocker la recherche
      this.searchOption = new Array<Options>();
      // On filtre le tablau d'options et on stock dans searchOption les résultats correspondant à la recherche
      this.searchOption = this.options.filter((option: Options) => option.text.toLowerCase().includes(this.filter.toLowerCase()));

      // On vérirife si le tableau de recherche est vide
      if (this.searchOption.length === 0) {
        this.closeList();
      } else {
        if (this.compteur === 0) {
          this.list = 'close';
        } else {
          this.list = 'stayOpen';
        }
        this.showList();
        if (e.code === 'ArrowDown') {
          if (this.compteur < this.searchOption.length - 1) {
            this.compteur++;
            this.temporyFilter = this.searchOption[this.compteur];
          }
        }
        if (e.code === 'ArrowUp') {
          if (this.compteur > 0) {
            this.compteur--;
            this.temporyFilter = this.searchOption[this.compteur];
          }
        }
        if (e.code === 'Enter') {
          this.compteur = 0;
          this.filter = this.temporyFilter.text;
          this.value = this.temporyFilter;
          if (this.isValue) {
            this.outData.emit(this.value.value);
          } else {
            this.outData.emit(this.value);
          }
          this.closeList();
          this.temporyFilter = new Options();
          this.compteur = 0;
        }
      }
    }

  }

  /**
   * Action de focus de l'input
   */
  public focus(): void {
    if (!this.list) {
      this.showList();
    }
    this.compteur = 0;
  }

  /**
   * Action au mouseover sur la liste
   */
  public mouseOver(): void {
    this.mouseIn = true;
  }

  /**
   * Action au mouseout sur la liste
   */
  public mouseOut(): void {
    this.mouseIn = false;
    this.temporyFilter = new Options();
    this.compteur = 0;
  }
  // #endregion

  // #region private fonctions

  /**
   * Evenement de fermeture de la liste
   */
  private closeList(): void {
    this.list = 'close';
    const body: HTMLElement = document.body;
    this.render.removeChild(body, this.listElement.nativeElement);
    this.render.addClass(this.listElement.nativeElement, 'd-none');
  }

  /**
   * récupération du positionnement de l'élément par rappot au screen
   */
  private getOffset(el: ElementRef): {left: number, top: number, right: number} {
    const rect: {left: number, top: number, right: number}  = el.nativeElement.getBoundingClientRect();
    return {
      left: rect.left + window.scrollX,
      top: rect.top + window.scrollY,
      right: rect.right
    };
  }

  // #endregion

}
